'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import CustomBadge from '@umodeapporg/ui/components/UI/CustomBadge';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { useAgente } from '@/domain/agente/hooks/useAgente';
import { PERMISSION } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { usePortalAcesso } from '@/domain/portal/hooks/usePortalAcesso';
import { cn } from '@/lib/utils';

const HOME_PATH = '/';

/**
 * Whether the launcher is on this screen: not on the home (it has its own line to the same
 * panel) and only for factory staff whose role opens the Início (a brand buyer only reaches the
 * Portal, and the API serves the agent under the Início's id, so it would refuse the panel).
 * The shell reads the same answer to keep the end of every page clear of the button.
 */
export function useAgenteFloatingButtonVisivel(): boolean {
  const pathname = usePathname();
  const { isStaff } = usePortalAcesso();
  const { can } = usePermissions();
  return pathname !== HOME_PATH && isStaff && can(PERMISSION.INICIO_VIEW);
}

/**
 * Round brand launcher pinned to the bottom-right corner; hides itself while the panel is open
 * and on the home, which carries its own line to open the very same panel (two launchers for
 * one conversation on one screen would be noise).
 */
export function AgenteFloatingButton() {
  const { t } = useTranslation('common');
  const { togglePainel, painelAberto, conversa } = useAgente();
  const visivel = useAgenteFloatingButtonVisivel();

  if (!visivel) {
    return null;
  }

  const pendentes = conversa.propostasPendentes.length;

  return (
    <div
      className={cn(
        'fixed bottom-5 right-5 z-40 transition-opacity duration-fast',
        painelAberto && 'pointer-events-none opacity-0',
      )}
    >
      <CustomBadge content={pendentes} invisible={pendentes === 0} tone="danger" className="ring-2 ring-canvas">
        <ControlledTooltip content={t('nav.agente')} placement="left">
          <ControlledButton
            defaultHtmlButton
            onClick={togglePainel}
            className="interactive flex size-14 items-center justify-center rounded-full bg-brand text-brand-fg shadow-lg"
          >
            <MaterialSymbol icon="auto_awesome" size={24} />
          </ControlledButton>
        </ControlledTooltip>
      </CustomBadge>
    </div>
  );
}
